import type { PointerElementDescriptor } from '@wegic/site-sdk/shared';
import type { TourData } from '@/components/TourMode/const';
import { getAbortFuncByUrlAndMethod } from '@wegic/request';
import { TOUR_DATA_STORAGE_PREFIX } from '@/components/TourMode/const';
import { tourRequest } from '@/services/request';

export interface ElementGuideSectionContext {
  id?: string;
  title?: string;
  description?: string;
  position?: number;
  total?: number;
}

export interface ElementGuideRequest {
  element: PointerElementDescriptor;
  section?: ElementGuideSectionContext;
  userLanguage?: string;
}

export interface ElementGuideResult {
  title: string;
  explanation: string;
}

export interface ElementGuideRequestHandle {
  promise: Promise<ElementGuideResult>;
  cancel: () => void;
}

const tourCacheEpochByProject = new Map<string, number>();
const TOUR_DATA_CACHE_VERSION = 2;

interface CachedTourData {
  version: number;
  userLanguage: string;
  data: TourData;
}

// ==================== localStorage 缓存 ====================

function getCachedTourData(projectId: string, userLanguage?: string): TourData | null {
  try {
    const raw = localStorage.getItem(`${TOUR_DATA_STORAGE_PREFIX}${projectId}`);
    if (!raw) {
      return null;
    }
    const cached = JSON.parse(raw) as Partial<CachedTourData>;
    if (
      cached.version === TOUR_DATA_CACHE_VERSION
      && cached.userLanguage === (userLanguage || '')
      && cached.data
    ) {
      return cached.data;
    }
    return null;
  } catch {
    return null;
  }
}

function cacheTourData(projectId: string, data: TourData, userLanguage?: string): void {
  try {
    localStorage.setItem(`${TOUR_DATA_STORAGE_PREFIX}${projectId}`, JSON.stringify({
      version: TOUR_DATA_CACHE_VERSION,
      userLanguage: userLanguage || '',
      data,
    } satisfies CachedTourData));
  } catch {
    // 存储失败（如超出配额）不影响正常流程
  }
}

/** 清除指定项目的导览缓存 */
export function clearTourDataCache(projectId: string): void {
  tourCacheEpochByProject.set(projectId, (tourCacheEpochByProject.get(projectId) ?? 0) + 1);
  try {
    localStorage.removeItem(`${TOUR_DATA_STORAGE_PREFIX}${projectId}`);
  } catch {
    // ignore
  }
}

// ==================== API ====================

/**
 * 获取导览数据
 * 优先读取本地缓存，无缓存时调用后端 AI 生成并持久化
 */
export async function generateTourData(
  projectId: string,
  userLanguage?: string,
): Promise<TourData> {
  const requestCacheEpoch = tourCacheEpochByProject.get(projectId) ?? 0;
  const cached = getCachedTourData(projectId, userLanguage);
  if (cached) {
    return cached;
  }

  const response = await tourRequest.post<TourData>(
    `/projects/v1/${projectId}/tour/generate`,
    userLanguage ? { userLanguage } : undefined,
  );
  const data = response.data;
  // A document reload can invalidate this request while it is still in flight.
  // Do not let an old DOM response overwrite the cache prepared for the repaired page.
  if ((tourCacheEpochByProject.get(projectId) ?? 0) === requestCacheEpoch) {
    cacheTourData(projectId, data, userLanguage);
  }
  return data;
}

/**
 * 生成当前指针停留元素的即时设计解读。
 * 独立 requestId 让快速切换元素时只中止上一条请求，不影响全站导览请求。
 */
export function generateElementGuide(
  projectId: string,
  requestId: string,
  payload: ElementGuideRequest,
): ElementGuideRequestHandle {
  const path = `/projects/v1/${projectId}/tour/element-guide?requestId=${encodeURIComponent(requestId)}`;
  const promise = tourRequest
    .post<ElementGuideResult>(path, payload)
    .then(response => response.data);

  return {
    promise,
    cancel: () => getAbortFuncByUrlAndMethod(path, 'post')(),
  };
}
